import {
  FieldItemList,
  RecipientBody,
  Notification,
  TableCells,
  TableFieldType,
  Option,
  FiscalYear,
  EntityRef,
  FiscalYearsRes,
  MenuItem,
  SortParams,
  Portal,
} from 'models';
import { Environment, PortalsName } from './enums';
import {
  get,
  toLower,
  map,
  flatten,
  each as _each,
  filter as _filter,
  isEqual,
  some as _some,
  orderBy as _orderBy,
  get as _get,
} from 'lodash';
import {
  format as formatDate,
  addDays,
  startOfYear,
  startOfMonth,
  startOfWeek,
  startOfDay,
  endOfYear,
  endOfMonth,
  endOfWeek,
  endOfDay,
  addMinutes,
  isAfter,
  formatDistanceStrict,
} from 'date-fns';
import { fi } from 'date-fns/locale';
import parse from 'html-react-parser';

export const serviceDateFormat = "yyyy-MM-dd'T'00:00:00.000";
export const dateTimeFormat = 'd.M.yyyy H:mm';
export const defaultDateFormat = 'd.M.yyyy';

export function customFormat(
  date: Date | number | null,
  formatString: 'server' | 'datetime' | 'default' = 'default'
): string | null {
  if (!date) return null;

  let f =
    formatString === 'server'
      ? serviceDateFormat
      : formatString === 'datetime'
      ? dateTimeFormat
      : defaultDateFormat;

  return formatDate(date, f);
}

export const eachMinutesOfInterval = (
  interval: { start: Date; end: Date },
  step: number
) => {
  let startTime = interval.start.getTime();
  let endTime = interval.end.getTime();

  if (!(startTime <= endTime)) {
    throw new RangeError('Invalid interval');
  }

  const dates: Date[] = [];
  let currentDate = interval.start;
  currentDate.setMinutes(0, 0, 0);

  while (currentDate.getTime() <= endTime) {
    dates.push(currentDate);
    currentDate = addMinutes(currentDate, step);
  }

  return dates;
};

export const toNumber = (num: number | null | undefined) => {
  if (!num) {
    return -Infinity;
  }

  return num;
};

export const _isNull = <T>(value: T) => {
  let res = value === null || undefined ? '' : value;
  return res;
};

export function formatListData<T = {}>(val: any, options: FieldItemList<T>) {
  switch (options.format) {
    case 'datetime':
      return val ? customFormat(new Date(val), 'datetime') : '';
    case 'date':
      return val ? customFormat(new Date(val)) : '';
    case 'boolean':
      return val ? options.variants[0] : options.variants[1];
    default:
      return val;
  }
}

export function createTabsConfig<
  T extends Array<{ Id: string; Name: string | null }>
>(
  data: T,
  notifications?: Notification[]
): Array<{
  id: string;
  label: string;
  warning: boolean;
}> {
  return data.map((item) => ({
    id: item.Id,
    label: item.Name || '',
    warning: notifications
      ? notifications.some((n) => n.Id === item.Id)
      : false,
  }));
}

export const createTranslationsConfig = (
  serverTranslations: Array<{
    Id: string;
    String: string;
  }>
) => {
  return serverTranslations.reduce((acc, next) => {
    acc[next.Id] = next.String;
    return acc;
  }, {} as { [key: string]: string });
};

// export function toDate(val: string): Date;
// export function toDate(val: null | undefined): null;
export function toDate(val: string | null | undefined) {
  return val ? new Date(val) : null;
}

export function buildSelectOptions<T>(
  data: T[],
  valueField: string,
  labelField: string
): Array<{ label: string; value: string | number }> {
  return data.map((item) => ({
    label: get(item, labelField) || '',
    value: get(item, valueField),
  }));
}

export const getShortWeeks = () => {
  const firstDOW = startOfWeek(new Date(), { weekStartsOn: 1 });

  return Array.from(Array(7)).map((e, i) =>
    formatDate(addDays(firstDOW, i), 'EEEEEE', { locale: fi })
  );
};

export const getMonthsList = () => {
  const months: string[] = [];

  for (let i = 0; i < 12; i++) {
    months.push(fi.localize?.month(i, { width: 'wide' }));
  }

  return months;
};

export const getYearsList = (from: number, to: number) => {
  const list: string[] = [];

  for (let i = from; i <= to; i++) {
    list.push(`${i}`);
  }

  return list;
};

export function cloneDate(date: Date): Date {
  return new Date(date.getTime());
}

export function formatTableData<T>(data: T, cell: TableCells<T>) {
  switch (cell.type) {
    case 'html':
      return get(data, cell.id) ? parse(get(data, cell.id)) : '';
    case 'datetime':
      return get(data, cell.id)
        ? formatDate(
            new Date(get(data, cell.id)),
            cell?.format || dateTimeFormat
          )
        : '';
    case 'date':
      return get(data, cell.id)
        ? formatDate(
            new Date(get(data, cell.id)),
            cell?.format || defaultDateFormat
          )
        : '';
    default:
      return get(data, cell.id);
  }
}

export function orderByType<T>(data: T, type: TableFieldType) {
  switch (type) {
    case 'string':
      return typeof data === 'string' ? data.toLowerCase() : data;
    case 'number':
      return typeof data === 'number'
        ? parseFloat(`${data as number}`)
        : -Infinity;
    case 'date':
      return data &&
        (typeof data === 'string' ||
          typeof data === 'number' ||
          data instanceof Date)
        ? new Date(data).getTime()
        : -Infinity;
    default:
      return data as string;
  }
}

export function sortList<T extends {}>(list: T[], sortParams: SortParams) {
  return _orderBy(
    list,
    (item) => orderByType(_get(item, sortParams.orderBy), sortParams.type),
    sortParams.order
  );
}

export function validateChatMessage(message: string) {
  let m = message.trim();
  return !!m && m.length < 2000;
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function extractOption(id: number | string | null, options: Option[]) {
  if (!id) {
    return options[0] || null;
  }

  return options.find((option) => option.Value === id) || null;
}

export function eventDuration(duration: number | null) {
  if (!duration) return '';

  let start = startOfDay(Date.now()),
    end = addMinutes(start, duration);

  return formatDistanceStrict(start, end, { locale: fi });
}

export function initDateTime(date: Date) {
  const newDate = new Date(date.getTime());

  newDate.setHours(0);
  newDate.setMinutes(0);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  return newDate;
}

export function createWeek(from: Date, to: Date) {
  let comparator = new Date(from.getTime());
  let week: Date[] = [];

  while (!isAfter(comparator, to)) {
    week.push(comparator);
    comparator = addDays(comparator, 1);
  }

  return week;
}

export const formatNum = (num: number | null | undefined) => {
  if (typeof num !== 'number') {
    num = 0;
  }

  let arr = num.toLocaleString('ru').split(','),
    f1 = '',
    f2 = '';

  if (arr.length === 1) {
    f1 = arr[0];
  }

  if (arr.length > 1) {
    f1 = arr[0];
    f2 = arr[1];
  }

  return `${f1},${f2.padEnd(2, '00')}`;
};

function searchByType(
  data: unknown,
  type: TableFieldType,
  searchTerm: string,
  searchKey?: string
): boolean {
  switch (type) {
    case 'string':
      return typeof data === 'string'
        ? toLower(data).includes(toLower(searchTerm))
        : false;
    case 'hashstring':
      return typeof data === 'string'
        ? toLower(`#${data}`).includes(toLower(searchTerm))
        : false;
    case 'number':
      return typeof data === 'number'
        ? formatNum(data).includes(searchTerm)
        : false;
    case 'datetime':
      return typeof data === 'string'
        ? formatDate(new Date(data), dateTimeFormat).includes(searchTerm)
        : false;
    case 'date':
      return typeof data === 'string'
        ? formatDate(new Date(data), defaultDateFormat).includes(searchTerm)
        : false;
    case 'list':
      return Array.isArray(data) && searchKey
        ? data.some((item) => {
            const searchKeyData = get(item, searchKey);

            return toLower(searchKeyData).includes(toLower(searchTerm));
          })
        : false;
    default:
      return false;
  }
}

export function filterAllBySearchTerm<T>(
  item: T,
  searchTerm: string,
  config: Array<TableCells<T>>
) {
  return _some(
    _filter(config, (c) => c.canSearch !== false),
    ({ id, type, searchKey }) =>
      searchByType(get(item, id), type, searchTerm, searchKey)
  );
}

export function extractKeysFromFieldItem<T extends object>(
  config: Array<FieldItemList<T>[]>
) {
  return map(flatten(config), (fieldItem) => fieldItem.key);
}

export function extractCurrentFiscalYear(
  fiscalYears: FiscalYear[] | undefined
) {
  if (!fiscalYears || !fiscalYears.length) {
    return null;
  }

  return fiscalYears[0];
}

export function createOptionFromEntity(
  entity: EntityRef | null
): Option | null {
  if (!entity) return null;

  return {
    Label: entity.Name,
    Value: entity.Id,
  };
}

export function createMassMessageRecipientBody(values: {
  CooperativesGroup: string[];
  SpacesGroup: string[];
  RecipientRoles: string[];
  IgnoredRecipientRoles: string[];
  SendEmail: boolean;
  SendFloorApp: boolean;
  SendPortalMessage: boolean;
  SendSms: boolean;
}): RecipientBody {
  if (!values)
    return {
      CooperativesIds: [],
      RecipientRolesIds: [],
      IgnoredRecipientRolesIds: [],
      SpacesIds: [],
      SendEmail: false,
      SendFloorApp: false,
      SendPortalMessage: false,
      SendSms: false,
    };

  let body: RecipientBody = {
    CooperativesIds: [],
    RecipientRolesIds: [],
    IgnoredRecipientRolesIds: [],
    SpacesIds: [],
    SendEmail: values.SendEmail || false,
    SendFloorApp: values.SendFloorApp || false,
    SendPortalMessage: values.SendPortalMessage || false,
    SendSms: values.SendSms || false,
  };

  if (values?.CooperativesGroup?.length) {
    _each(values.CooperativesGroup, (id) => body.CooperativesIds.push(id));
  }

  if (values?.SpacesGroup?.length) {
    _each(values.SpacesGroup, (id) => body.SpacesIds.push(id));
  }

  if (values?.RecipientRoles?.length) {
    _each(values.RecipientRoles, (id) => body.RecipientRolesIds.push(id));
  }

  if (values?.IgnoredRecipientRoles?.length) {
    _each(values.IgnoredRecipientRoles, (id) =>
      body.IgnoredRecipientRolesIds.push(id)
    );
  }

  return body;
}

export const filterBalanceFiscalYears = (
  fiscalYearsData: FiscalYearsRes | undefined,
  comparator: Date
) => {
  if (!fiscalYearsData) {
    return [];
  }

  const dateComparator = initDateTime(comparator);

  return _filter(fiscalYearsData.FiscalYears, (FiscalYear) => {
    const startDate = toDate(FiscalYear.StartDate);

    if (!startDate) return false;

    return (
      isAfter(dateComparator, startDate) || isEqual(dateComparator, startDate)
    );
  });
};

export const filterFiscalYears = (
  fiscalYearsData: FiscalYearsRes | undefined
) => {
  if (!fiscalYearsData) {
    return [];
  }

  return _filter(fiscalYearsData.FiscalYears, (FiscalYear) => {
    const startDate = toDate(FiscalYear.StartDate);
    const closedDate = toDate(fiscalYearsData.ClosedPeriodEnd);

    if (!closedDate) {
      return true;
    }

    if (!startDate) {
      return false;
    }

    return isAfter(closedDate, startDate) || isEqual(closedDate, startDate);
  });
};

export function getFiscalYearsStartDate(
  startDate: Date | null,
  closedPeriodEndDate: Date | null
) {
  return closedPeriodEndDate && startDate
    ? isAfter(closedPeriodEndDate, startDate)
      ? startDate
      : closedPeriodEndDate
    : startDate;
}

export function getFiscalYearsEndDate(
  endDate: Date | null,
  closedPeriodEndDate: Date | null
) {
  return closedPeriodEndDate && endDate
    ? isAfter(closedPeriodEndDate, endDate)
      ? endDate
      : closedPeriodEndDate
    : endDate;
}

export function toUpperFirstChar(str: string) {
  return `${str.substr(0, 1).toUpperCase()}${str.substr(1).toLowerCase()}`;
}

export function isObj(data: any) {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}

export const pipe =
  (...fns: Function[]) =>
  (x: any) =>
    fns.reduce((v, f) => f(v), x);

export const mode = {
  dev: () => process.env.REACT_APP_CURRENT_ENV === Environment.Dev,
  perf: () => process.env.REACT_APP_CURRENT_ENV === Environment.Perfomance,
  release: () => process.env.REACT_APP_CURRENT_ENV === Environment.Release,
  prod: () => process.env.REACT_APP_CURRENT_ENV === Environment.Production,
  strict: function () {
    return this.release() || this.prod();
  },
  nonStrict: function () {
    return this.dev() || this.perf();
  },
};

export const disabledProdConsole = () => {
  if (mode.strict()) console.log = () => {};
};

export const toMoneyFormat = (num: number) => {
  let arr = num.toLocaleString('ru').split(','),
    f1 = '',
    f2 = '';

  if (arr.length === 1) {
    f1 = arr[0];
  }

  if (arr.length > 1) {
    f1 = arr[0];
    f2 = arr[1];
  }

  return `${f1},${f2.padEnd(2, '00')}`;
};

export const getBalanceEndDate = (d: Date) => {
  const currentDate = initDateTime(new Date());
  return isAfter(d, currentDate) ? currentDate : d;
};

export const showBoardMessageNotifications = (menu: MenuItem[]) => {
  return menu.some((item) => item.Id === 'board-eventsinformation');
};

export function getRouteByPortal(portal: Portal | null) {
  switch (portal?.Name) {
    case 'Investor':
      return '/investor';
    default:
      return '/';
  }
}

export function isMaintenancePortal(portal: Portal | null) {
  return portal?.Name === PortalsName.Maintenance;
}

export function isDefaultPortal(portal: Portal | null) {
  return portal?.Name === PortalsName.Default;
}

export function isInvestorPortal(portal: Portal | null) {
  return portal?.Name === PortalsName.Investor;
}

export function blobToBase64(
  blob: Blob,
  options: { stripMIMEType: boolean } = { stripMIMEType: false }
) {
  const { stripMIMEType } = options;

  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      let base64 = reader.result;

      if (stripMIMEType) {
        base64 = (reader.result as string).substr(
          (reader.result as string).indexOf(',') + 1
        );
      }
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });
}

export function blobToArrayBuffer(blob: Blob) {
  return new Promise((resolve, _) => {
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(blob);
    fileReader.onload = function (event) {
      let arrayBuffer = fileReader.result;

      resolve(arrayBuffer);
    };
  });
}
